import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  FormLabel,
  Input,
  Text,
  FormControl,
  FormErrorMessage,
  Alert,
  Image,
  AlertIcon,
  AlertTitle,
  InputGroup,
  InputRightElement,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
} from "@chakra-ui/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import { signInSchema } from "../utils/formValidationSchema";
import { useDispatch, useSelector } from "react-redux";
import {
  addLoginUser,
  fetchUserProfile,
  fetchMemberUserProfile,
} from "../redux/slices/authSlice";
import { allPermissions } from "../redux/slices/authPermissionsSlice";
import { signIn } from "../services/authService";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import LoginImage from "../components/LoginImage";
import logo from "../Assets/Images/ncs-logo.svg";

const Login = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [error, setError] = useState(null);
  const errorsArray = Array.isArray(error) ? error : [error];
  const [isLoading, setIsLoading] = useState(false);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const userType = useSelector((state) => state.auth?.user?.userType);

  const initialValues = {
    email: "",
    password: "",
  };

  useEffect(() => {
    if (auth?.user) {
      if (userType === "Administrative") {
        return navigate("/dashboard");
      } else if (userType === "IndividualApplicant") {
        return navigate("/individual-reg-form");
      } else if (userType === "StudentApplicant") {
        return navigate("/student-reg-form");
      } else if (userType === "CorporateApplicant") {
        return navigate("/corporate-reg-form");
      } else if (userType === "Member") {
        return navigate("/member/dashboard");
      }
    }
  }, [auth?.user, navigate, userType]);

  const handleSubmit = async (values) => {
    setError("");
    setIsLoading(true);
    try {
      const response = await signIn(values);

      // console.log("Login Response: ", response.userType);
      if (response?.isSuccess === true) {
        setIsLoading(false);
        if (userType === "Administrative") {
          return navigate("/dashboard");
        } else if (userType === "IndividualApplicant") {
          return navigate("/individual-reg-form");
        } else if (userType === "StudentApplicant") {
          return navigate("/student-reg-form");
        } else if (userType === "CorporateApplicant") {
          return navigate("/corporate-reg-form");
        } else if (userType === "Individual") {
          return navigate("/member/dashboard");
        } else if (userType === "Corporate") {
          return navigate("/member/dashboard");
        }
        dispatch(addLoginUser(response));
        dispatch(allPermissions());
        dispatch(fetchUserProfile());
        dispatch(fetchMemberUserProfile());
      } else {
        setIsLoading(false);
        setError(
          response?.response?.data?.errors?.map((errors) => errors) ||
            response?.response?.data?.error?.description ||
            response?.error?.description ||
            "Failed to login, please try again"
        );
      }
    } catch (error) {
      setError(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex
      minH={"100vh"}
      w="full"
      sx={{
        overflowY: "scroll",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "-ms-overflow-style": "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
      overflowY={"auto"}
    >
      <LoginImage />
      <Flex
        // h={"full"}
        // w="full"
        justify={"center"}
        position={"relative"}
        alignItems={"center"}
        flexDirection={"column"}
      >
        <Flex
          justifyContent="flex-start"
          alignItems="center"
          gap={1}
          mt={5}
          px={["2rem", "3rem", "4rem"]}
        >
          <Image src={logo} alt="ncs logo" />
          <Box />
        </Flex>

        <Flex
          direction={"column"}
          bg={"white"}
          width={"full"}
          px={["2rem", "3rem", "4rem"]}
          pt="3rem"
          pb="2rem"
        >
          <Flex justify={"space-between"} direction={"column"} mb="1.5rem">
            <Text fontWeight={"700"} fontSize={"1.8rem"}>
              Login
            </Text>
            <Text fontSize={"14px"} pt={2}>
              Kindly fill in your login details below to login into your Account
            </Text>
          </Flex>

          {error && (
            <Alert status="error" className="mt-3" mb={5}>
              <AlertIcon />
              <AlertTitle>{errorsArray.join(", ")}</AlertTitle>
            </Alert>
          )}

          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={signInSchema}
            onSubmit={(values) => handleSubmit(values)}
          >
            {({ errors, touched, handleBlur, handleChange }) => (
              <Form mt={4}>
                <FormControl
                  isInvalid={errors.email && touched.email}
                  mb={5}
                  color="black"
                >
                  <FormLabel>Email</FormLabel>
                  <Input
                    placeholder="Enter email address"
                    bgColor={"#F3F3F3"}
                    borderRadius={"8px"}
                    borderColor={"transparent"}
                    mb="1rem"
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  {touched.email && errors.email && (
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  )}
                </FormControl>

                <FormControl
                  isInvalid={errors.password && touched.password}
                  // mb={5}
                  color="black"
                >
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <Input
                      pr="4.5rem"
                      type={show ? "text" : "password"}
                      placeholder="Enter password"
                      borderRadius={"8px"}
                      name="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      bgColor={"#F3F3F3"}
                    />
                    <InputRightElement width="4.5rem">
                      <Button
                        h="1.75rem"
                        size="sm"
                        background={"none"}
                        onClick={() => setShow(!show)}
                      >
                        {show ? (
                          <BsEye w={5} h={5} color="GrayText" />
                        ) : (
                          <BsEyeSlash color={"GrayText"} w={5} h={5} />
                        )}
                      </Button>
                    </InputRightElement>
                  </InputGroup>

                  {touched.password && errors.password && (
                    <FormErrorMessage>{errors.password}</FormErrorMessage>
                  )}
                </FormControl>
                <Flex justify={"flex-end"}>
                  <Button
                    color={"#23A455CC"}
                    variant={"ghost"}
                    alignItems={"center"}
                    fontSize={"sm"}
                    fontWeight={"normal"}
                    alignSelf={"start"}
                    p={0}
                    _hover={{ bg: "transparent" }}
                    onClick={() => navigate("/forgot-password")}
                  >
                    <Text>Forgot password</Text>
                  </Button>
                </Flex>

                <Button
                  isLoading={isLoading}
                  loadingText="Logging in ..."
                  color={"white"}
                  bgColor={"#23A455CC"}
                  borderRadius="12px"
                  mt={"2rem"}
                  mb="1rem"
                  w={"full"}
                  type="submit"
                  _hover={{ bgColor: "" }}
                >
                  Login
                </Button>
              </Form>
            )}
          </Formik>
        </Flex>
        <Divider py={"-10"} color={"#8A8C8E"} border={"1px solid #8A8C8E"} />
        <Text pt={6} pb={10} fontSize={"14px"} fontWeight={"400"}>
          Not a member yet?{" "}
          <span style={{ color: "#23A455CC", cursor: "pointer" }}>
            <Menu>
              <MenuButton>Register Here</MenuButton>
              <MenuList>
                <MenuItem
                  onClick={() => navigate("/individual-registration")}
                  color="black"
                >
                  As Individual
                </MenuItem>
                <MenuItem
                  onClick={() => navigate("/student-registration")}
                  color="black"
                >
                  As Student
                </MenuItem>
                <MenuItem onClick={() => navigate("/corporate")} color="black">
                  As Corporate Organization
                </MenuItem>
              </MenuList>
            </Menu>
          </span>
        </Text>
      </Flex>
    </Flex>
  );
};

export default Login;
